// Phase 3 — Excel (.xlsx) export of the Répartition Report Results tables.
//
// Client-confirmed (17.07.2026): a native-data Excel export sitting next to the
// existing "Download PDF" button. Graphs stay PDF-only; this file emits the four
// Results sheet types exactly as they appear in the client's reference workbook
// `1007.1_Plaines-6_Résultats-2425_20260629.xlsx`:
//   1. "Module"    — Résultats par module         (from resultsPerModuleData)
//   2. "Objet"     — Résultats par objet          (from resultsPerObjectData)
//   3. "Locataire" — Résultats par locataire      (from resultsPerTenantData)
//   4. one tab per object — Résultat détaillé      (from objectDetailData.details)
//
// Design notes:
//   * All figures are already computed on the backend (subtotals, grand totals,
//     cost lines, consumption). This builder only lays them into cells — no math.
//   * SheetJS CE has NO cell-styling API (no bold/borders/fills), only column
//     widths (!cols) and per-cell number formats (z). The reference file itself
//     uses only widths + raw numbers, so we target a DATA-faithful match.
//   * Numbers are written as raw JS numbers (not fmtNum strings) so Excel treats
//     them as numeric and applies the 2-decimal display format. Null/blank cells
//     are left empty (mirrors the report's "-").
//   * The Acomptes/Solde columns (Objet/Locataire) and rows (détaillé) follow the
//     per-period `acomptesEnabled` toggle, exactly like the PDF/screen.

// xlsx-js-style is a drop-in fork of SheetJS 0.18.5 with the identical API plus a
// working per-cell `s` (style) property. The plain `xlsx` CE build cannot write any
// cell styling (bold/color/fill), which is why we use the fork here. Other screens
// (import/upload) keep using plain `xlsx` for READING files.
import * as XLSX from "xlsx-js-style";

import { fmtDate, fmtPeriod, fmtYearFile, objetDetailLabels } from "./templates/formatters";

// Brand navy (matches the PDF report's #00033F and the reference .xlsx's FF000A40).
// Header rows and all Total/subtotal/grand-total rows use BOLD navy text, no fill —
// exactly like the client's reference file. Data rows stay unstyled (normal weight).
const NAVY = "00033F";
const HEADER_STYLE = { font: { bold: true, color: { rgb: NAVY }, sz: 9 } };
// Kept separate from HEADER_STYLE so totals can diverge later (e.g. a top border).
const TOTAL_STYLE = { font: { bold: true, color: { rgb: NAVY }, sz: 9 } };

// Wrap a cell value with a style, preserving numeric type/format when present.
// A styled string → {v, t:"s", s}; a styled numeric cell keeps {t:"n", v, z, s}.
const styled = (cell, s) =>
  cell && typeof cell === "object" ? { ...cell, s } : { v: cell ?? "", t: "s", s };

// Map every cell in a row through `styled` — used for header and total rows only.
const styleRow = (cells, s) => cells.map((c) => styled(c, s));

// A numeric cell object, or "" when the value is absent (→ empty cell, like "-").
// Zero is preserved as a real 0 here because the reference file writes literal 0s
// in the cost table (e.g. "Frais de nettoyage" lines); the on-screen "-" is a
// display-only choice we do not carry into the raw data export.
const num = (v, decimals = 2) => {
  if (v === null || v === undefined) return "";
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  // Build the "#,##0.00…" format string for the requested decimal count.
  const z = decimals > 0 ? `#,##0.${"0".repeat(decimals)}` : "#,##0";
  return { t: "n", v: n, z };
};

// Plain text/empty passthrough.
const txt = (v) => (v === null || v === undefined ? "" : String(v));

// Excel worksheet-name rules: ≤31 chars, none of  [ ] : * ? / \  and no leading/
// trailing apostrophe. We also de-duplicate collisions with a -2/-3 suffix, which
// is exactly what the reference workbook does (e.g. "2691.03.0002.01-2").
const sanitizeSheetName = (raw) => {
  let name = String(raw || "").replace(/[[\]:*?/\\]/g, " ").trim();
  if (!name) name = "Sheet";
  if (name.length > 31) name = name.slice(0, 31);
  // Strip leading/trailing apostrophes that Excel forbids.
  return name.replace(/^'+|'+$/g, "") || "Sheet";
};

const uniqueSheetName = (raw, used) => {
  const base = sanitizeSheetName(raw);
  if (!used.has(base)) {
    used.add(base);
    return base;
  }
  for (let i = 2; ; i += 1) {
    const suffix = `-${i}`;
    // Keep the total ≤31 by trimming the base to make room for the suffix.
    const candidate = `${base.slice(0, 31 - suffix.length)}${suffix}`;
    if (!used.has(candidate)) {
      used.add(candidate);
      return candidate;
    }
  }
};

// Attach a worksheet built from an array-of-arrays, with optional column widths.
const appendAoaSheet = (wb, aoa, sheetName, cols) => {
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  if (cols) ws["!cols"] = cols;
  XLSX.utils.book_append_sheet(wb, ws, sheetName);
  return ws;
};

// ─── Sheet 1: Module ─────────────────────────────────────────────────────────
const buildModuleSheet = (wb, data) => {
  if (!data) return;
  const modules = data.modules || [];
  const totals = data.totals || {};
  const aoa = [
    ["Résultats par module"],
    [],
    styleRow([
      "Numéro", "Module", "Surface m2", "HT", "CHF/m2", "TTC",
      "PP HT", "Diff. HT", "Diff. %", "Part %",
    ], HEADER_STYLE),
    ...modules.map((m) => [
      txt(m.numero), txt(m.module), num(m.surface_m2), num(m.main_ht),
      num(m.chf_m2), num(m.main_ttc), num(m.compare_ht), num(m.diff),
      num(m.diff_pct), num(m.part_pct),
    ]),
    styleRow([
      "", "Total", num(totals.surface_m2), num(totals.main_ht), "",
      num(totals.main_ttc), num(totals.compare_ht), "", "", "",
    ], TOTAL_STYLE),
  ];
  appendAoaSheet(wb, aoa, "Module", [
    { wch: 10 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 10 },
    { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 10 },
  ]);
};

// ─── Shared builder for Objet & Locataire (same 17-column layout) ─────────────
// `data` = { objects, subtotals, grand_total }. `groupKey` selects which subtotal
// field labels the "Total <…>" rows and how objects are grouped for insertion.
// Header labels copied from the reference workbook's "Objet"/"Locataire" row 3
// (plain "HT"/"TTC", no year prefix). The reference's "Log." (unit label) and
// "étage" (floor) columns are DROPPED per client decision (17.07.2026): neither
// exists anywhere in the app's data model, and the PDF/on-screen report has no
// such columns either — so we omit them rather than emit empty columns.
// Client 2026-07-23: column C was labelled "Ref." and filled with the raw
// reference (e.g. "710300.496 011.1"), but the reference workbook and the PDF
// both use "Objet" here — the derived unit code (e.g. "011.1"). Header +
// data source updated to match.
const OBJET_HEADERS = [
  "Numéro", "Type", "Objet", "locataire 1", "genre d'objet",
  "début loc.", "Surface", "Surface m2",
  "FEE début", "FEE fin", "HT", "CHF/m2", "TTC",
];

// Column widths for the objet/locataire layout (13 base + 2 acomptes).
const OBJET_COLS = [
  { wch: 10 }, { wch: 14 }, { wch: 18 }, { wch: 22 }, { wch: 16 },
  { wch: 12 }, { wch: 10 }, { wch: 11 }, { wch: 12 }, { wch: 12 },
  { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
];

// One data row for an object. Column order matches OBJET_HEADERS (Log./étage
// dropped). "début loc." reuses the lease start (fee_start).
const objetRow = (o, acomptesEnabled) => {
  const row = [
    txt(o.numero), txt(o.type), txt(o.objet_code), txt(o.locataire),
    txt(o.genre_objet),
    fmtDateCell(o.fee_start), // début loc.
    num(o.surface), num(o.surface_m2),
    fmtDateCell(o.fee_start), fmtDateCell(o.fee_end),
    num(o.ht), num(o.chf_m2), num(o.ttc),
  ];
  if (acomptesEnabled) row.push(num(o.acomptes), num(o.solde));
  return row;
};

// Dates are written as DD.MM.YYYY text (readable, matches the report display).
const fmtDateCell = (v) => {
  const s = fmtDate(v);
  return s === "-" ? "" : s;
};

// Subtotal / grand-total row: label in col A, figures aligned under their columns.
// 0-based indices (13-col base): Surface m2 = 7, HT = 10, TTC = 12, Acomptes = 13,
// Solde = 14.
const totalRow = (label, s, acomptesEnabled) => {
  const row = [
    label, "", "", "", "", "", "", num(s.surface_m2), "", "",
    num(s.ht), "", num(s.ttc),
  ];
  if (acomptesEnabled) row.push(num(s.acomptes), num(s.solde));
  return styleRow(row, TOTAL_STYLE);
};

const buildObjectLikeSheet = (wb, data, sheetName, title, groupField, acomptesEnabled) => {
  if (!data) return;
  const objects = data.objects || [];
  const subtotals = data.subtotals || [];
  const grand = data.grand_total || {};

  const header = [...OBJET_HEADERS];
  if (acomptesEnabled) header.push("Acomptes", "Solde");

  const aoa = [[title], [], styleRow(header, HEADER_STYLE)];

  // Group objects in payload order, appending a subtotal row after each group.
  // Objects arrive pre-sorted by the backend (per-module for objet, per-tenant for
  // locataire), so we walk them and flush a subtotal whenever the group key changes.
  const subByKey = new Map();
  for (const s of subtotals) subByKey.set(String(s[groupField]), s);

  const groupOf = (o) =>
    groupField === "module_code" ? String(o.numero) : String(o.tenant_name || "Vacant");

  let currentKey = null;
  const flushSubtotal = (key) => {
    const s = subByKey.get(key);
    if (!s) return;
    const label = groupField === "module_code"
      ? `Total ${s.module_name ?? key}`
      : `Total ${s.tenant_name ?? key}`;
    aoa.push(totalRow(label, s, acomptesEnabled));
  };

  for (const o of objects) {
    const key = groupOf(o);
    if (currentKey !== null && key !== currentKey) flushSubtotal(currentKey);
    aoa.push(objetRow(o, acomptesEnabled));
    currentKey = key;
  }
  if (currentKey !== null) flushSubtotal(currentKey);

  // Grand total.
  aoa.push(totalRow("Total général", grand, acomptesEnabled));

  const cols = acomptesEnabled ? OBJET_COLS : OBJET_COLS.slice(0, 15);
  appendAoaSheet(wb, aoa, sheetName, cols);
};

// ─── Sheet type 4: one Résultat détaillé sheet per group ─────────────────────
// `variant` selects the header block, exactly as ObjectDetail.jsx does for the PDF:
//   object → Objet / Locataire / Module / Période / Surface m2   (5 rows, unchanged)
//   module → Module / Période / Surface m2                       (3 rows)
// Tab name = the group's natural identity (objet_code, or the module code), which
// is what the client's own mock-up used ("90010").
const buildObjectDetailSheets = (wb, data, periodLabel, acomptesEnabled, usedNames, variant = 'object') => {
  if (!data) return;
  const details = data.details || [];
  // Objet tabs: a code imported on two modules has one entry per part, so a
  // repeated code is suffixed with its module ("06862.20.2100.07 (90710)") rather
  // than left to uniqueSheetName's meaningless "-2". Same labels as the PDF outline.
  const objetLabels = objetDetailLabels(details);
  for (const [entryIdx, d] of details.entries()) {
    const isModule = variant === 'module';
    const isTenant = variant === 'tenant';
    const TITLES = { module: 'Résultat détaillé (module)', tenant: 'Résultat détaillé (locataire)', object: 'Résultat détaillé (objet)' };
    // Header block per variant, mirroring ObjectDetail.jsx exactly. A tenant entry
    // has objet_code and module NULLED by the backend (a tenant spans modules), so
    // it must not reuse the objet rows — it lists Objets/Modules instead. Unlike the
    // PDF there is no ≤6 truncation here: a spreadsheet cell can hold the full list,
    // and losing data in a data export would be worse than a long cell.
    let headRows;
    if (isModule) {
      headRows = [[styled("Module", HEADER_STYLE), txt(d.module)]];
    } else if (isTenant) {
      headRows = [
        [styled("Locataire", HEADER_STYLE), txt(d.locataire)],
        [styled("Objets", HEADER_STYLE), txt((d.objets || []).join("  "))],
        [styled("Modules", HEADER_STYLE), txt((d.modules || []).join("  "))],
      ];
    } else {
      headRows = [
        [styled("Objet", HEADER_STYLE), txt(d.objet_code)],
        [styled("Locataire", HEADER_STYLE), txt(d.locataire)],
        [styled("Module", HEADER_STYLE), txt(d.module)],
      ];
    }
    const aoa = [
      [TITLES[variant] || TITLES.object],
      // Header-block: bold-navy label cell (col A), plain value beside it.
      ...headRows,
      [styled("Période", HEADER_STYLE), d.fee_start || d.fee_end ? fmtPeriod(d.fee_start, d.fee_end) : txt(periodLabel)],
      [styled("Surface m2", HEADER_STYLE), num(d.surface_m2)],
      [],
      styleRow(["Compte", "Désignation", "HT", "CHF/m2", "TTC", "Total HT", "Part %"], HEADER_STYLE),
      ...(d.cost_lines || []).map((c) => [
        txt(c.cost), txt(c.designation), num(c.ht), num(c.chf_m2),
        num(c.ttc), num(c.total_ht), num(c.part_pct),
      ]),
    ];

    const t = d.total || {};
    aoa.push(styleRow([
      "", "Total CHF", num(t.ht), num(t.chf_m2), num(t.ttc),
      num(t.total_ht_all), num(t.part_pct),
    ], TOTAL_STYLE));
    if (acomptesEnabled) {
      aoa.push(styleRow(["", "", "", "Acomptes", num(d.acomptes), "", ""], TOTAL_STYLE));
      aoa.push(styleRow(["", "", "", "Solde", num(d.solde), "", ""], TOTAL_STYLE));
    }

    // Consumption block. Skipped ENTIRELY — spacer row and header included — when the
    // building has no Consommations lines to show, so the Excel matches the PDF.
    // Client 2026-09-04 reported the PDF printing a lone header after every line was
    // switched off; the same unconditional header was here.
    if ((d.consommations || []).length > 0) {
      aoa.push([]);
      aoa.push(styleRow(["Consommations", "Unités", "CHF/unité", "", "Total Immeuble", "Part %"], HEADER_STYLE));
      for (const c of d.consommations) {
        aoa.push([
          `${txt(c.energy)}${c.unit ? ` ${c.unit}` : ""}`.trim(),
          num(c.object_value), num(c.chf_unite), "", num(c.total_immeuble), num(c.part_pct),
        ]);
      }
    }

    // Tab name = the group's natural identity, matching the client's mock-up tabs
    // ("90010" for a module, "HORECA SERVICE SARL" for a tenant). uniqueSheetName
    // handles the 31-char limit and any collision.
    let rawName;
    if (isModule) {
      rawName = String(d.module || "Module").split(" - ")[0];
    } else if (isTenant) {
      rawName = d.locataire || "Locataire";
    } else {
      rawName = objetLabels[entryIdx];
    }
    const sheetName = uniqueSheetName(rawName, usedNames);
    appendAoaSheet(wb, aoa, sheetName, [
      { wch: 12 }, { wch: 24 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 14 }, { wch: 10 },
    ]);
  }
};

// ─── Additional report sections (client asked for ALL page data, 17.07.2026) ──
// These mirror the on-screen report sections that were previously PDF-only. Each
// is TABLE-ONLY (all charts were already removed from these templates). Year
// labels come from `periodYears`; compare columns are always shown (they render
// blank when no prior period). Placed BEFORE the Results sheets to match page order.

// Section: "Modules" (ModulesSummary) — modulesData = { modules, totals }.
const buildModulesSummarySheet = (wb, data, years) => {
  if (!data) return;
  const modules = data.modules || [];
  const totals = data.totals || {};
  const cur = years?.current ?? "";
  const pri = years?.prior ?? "";
  const aoa = [
    ["Modules"],
    [],
    styleRow(["Numéro", "Module", cur, pri, "Diff.", "%", "Info"], HEADER_STYLE),
    ...modules.map((m) => [
      txt(m.surface), txt(m.name), num(m.main_value), num(m.compare_value),
      num(m.diff), num(m.diff_pct), txt(m.unit),
    ]),
    styleRow(["", "Total m²", num(totals.main_value), num(totals.compare_value),
      num(totals.diff), num(totals.diff_pct), ""], TOTAL_STYLE),
  ];
  appendAoaSheet(wb, aoa, "Modules", [
    { wch: 10 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 8 }, { wch: 10 },
  ]);
};

// Section: "Surfaces" (SurfacesList) — surfacesData = { surfaces, subtotals, grand_total }.
// Grouped by module_code with a subtotal after each group + a grand total.
const buildSurfacesSheet = (wb, data) => {
  if (!data) return;
  const surfaces = data.surfaces || [];
  const subtotals = data.subtotals || [];
  const grand = data.grand_total;
  const subByModule = new Map();
  for (const s of subtotals) subByModule.set(String(s.module_code), s);

  const header = styleRow([
    "Numéro", "Type", "Objet", "Locataire", "Référence", "genre d'objet",
    "Étage", "Surface", "Surface m2", "FEE début", "FEE fin",
  ], HEADER_STYLE);
  const aoa = [["Surfaces"], [], header];

  // Surface m2 is column index 8 (0-based) — subtotal value lands there.
  const subRow = (label, total) =>
    styleRow([label, "", "", "", "", "", "", "", num(total), "", ""], TOTAL_STYLE);

  let cur = null;
  const flush = (code) => {
    const s = subByModule.get(code);
    if (s) aoa.push(subRow(`Total ${s.module_name ?? code}`, s.total));
  };
  for (const s of surfaces) {
    const code = String(s.module_code);
    if (cur !== null && code !== cur) flush(cur);
    aoa.push([
      txt(s.module_code), txt(s.module_name), txt(s.objet_code), txt(s.tenant_name),
      txt(s.reference), txt(s.genre_objet), txt(s.etage),
      num(s.full_surface_value), num(s.prorated_surface_value),
      fmtDateCell(s.fee_start), fmtDateCell(s.fee_end),
    ]);
    cur = code;
  }
  if (cur !== null) flush(cur);
  aoa.push(subRow("Total général", grand));

  appendAoaSheet(wb, aoa, "Surfaces", [
    { wch: 10 }, { wch: 16 }, { wch: 16 }, { wch: 22 }, { wch: 20 }, { wch: 16 },
    { wch: 14 }, { wch: 10 }, { wch: 11 }, { wch: 12 }, { wch: 12 },
  ]);
};

// Section: "Résumé des coûts" (CostSummary) — costSummaryData = { costs, totals }.
const buildCostSummarySheet = (wb, data, years) => {
  if (!data) return;
  const costs = data.costs || [];
  const totals = data.totals || {};
  const cur = years?.current ?? "";
  const pri = years?.prior ?? "";
  const aoa = [
    ["Résumé des coûts"],
    [],
    styleRow(["Compte", "Désignation", "Clé", `${cur} HT`, `${cur} TTC`,
      `${pri} HT`, "Diff. HT", "Diff. %", "Part %"], HEADER_STYLE),
    ...costs.map((c) => [
      txt(c.cost), txt(c.name), txt(c.key_name), num(c.main_ht), num(c.main_ttc),
      num(c.compare_ht), num(c.diff), num(c.diff_pct), num(c.part_pct),
    ]),
    styleRow(["", "Total CHF", "", num(totals.main_ht), num(totals.main_ttc),
      num(totals.compare_ht), num(totals.diff), num(totals.diff_pct), ""], TOTAL_STYLE),
  ];
  appendAoaSheet(wb, aoa, "Résumé des coûts", [
    { wch: 10 }, { wch: 26 }, { wch: 16 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
    { wch: 12 }, { wch: 10 }, { wch: 10 },
  ]);
};

// Section: "Coûts" (CostDetails) — costDetailsData = { details, per_invoice_data_available, totals }.
// Two mutually-exclusive layouts depending on per-invoice availability.
const buildCostDetailsSheet = (wb, data) => {
  if (!data) return;
  const details = data.details || [];
  const totals = data.totals || {};
  const perInvoice = !!data.per_invoice_data_available;

  if (perInvoice) {
    const aoa = [
      ["Coûts"],
      [],
      styleRow(["Compte", "Désignation", "Date", "Nr facture", "Description 1",
        "Quantité", "Prix HT", "Montant HT", "TVA %", "TVA", "Montant TTC"], HEADER_STYLE),
    ];
    for (const acct of details) {
      const invoices = acct.invoices || [];
      let qtySum = 0;
      for (const inv of invoices) {
        qtySum += Number(inv.quantity) || 0;
        aoa.push([
          txt(acct.cost), txt(acct.name), fmtDateCell(inv.date), txt(inv.invoice_number),
          txt(inv.libelle), num(inv.quantity, 0), num(inv.price_ht, 4), num(inv.amount_ht),
          num(inv.tva_pct), num(inv.tva), num(inv.amount_ttc),
        ]);
      }
      const sub = acct.subtotal || {};
      const subLabel = `${acct.orphan ? "(orphan) " : ""}Total ${acct.name || acct.cost}`;
      aoa.push(styleRow([
        subLabel, "", "", "", "", num(qtySum, 0), "", num(sub.ht),
        "", num((Number(sub.ttc) || 0) - (Number(sub.ht) || 0)), num(sub.ttc),
      ], TOTAL_STYLE));
    }
    aoa.push(styleRow([
      "Total général", "", "", "", "", "", "", num(totals.ht), "", "", num(totals.ttc),
    ], TOTAL_STYLE));
    appendAoaSheet(wb, aoa, "Coûts", [
      { wch: 10 }, { wch: 24 }, { wch: 11 }, { wch: 14 }, { wch: 26 }, { wch: 10 },
      { wch: 12 }, { wch: 13 }, { wch: 8 }, { wch: 11 }, { wch: 13 },
    ]);
    return;
  }

  // Aggregate mode.
  const aoa = [
    ["Coûts"],
    [],
    styleRow(["Compte", "Désignation", "Clé", "Montant HT", "Montant TTC"], HEADER_STYLE),
    ...details.map((acct) => [
      txt(acct.cost), txt(acct.name), txt(acct.type),
      num(acct.subtotal?.ht), num(acct.subtotal?.ttc),
    ]),
    styleRow(["Total général", "", "", num(totals.ht), num(totals.ttc)], TOTAL_STYLE),
  ];
  appendAoaSheet(wb, aoa, "Coûts", [
    { wch: 10 }, { wch: 26 }, { wch: 16 }, { wch: 14 }, { wch: 14 },
  ]);
};

// Section: "Compteurs" (MetersComparison) — metersData = { meters }. No totals.
const buildMetersSheet = (wb, data, years) => {
  if (!data) return;
  const meters = data.meters || [];
  const cur = years?.current ?? "";
  const pri = years?.prior ?? "";
  const aoa = [
    ["Compteurs"],
    [],
    styleRow(["Numéro", "Désignation", "Énergie", "Compteur", "Lecture", cur, "Unité",
      pri, "Différence", "%"], HEADER_STYLE),
    ...meters.map((m) => [
      txt(m.consumption), txt(m.name), txt(m.energie), txt(m.compteur),
      num(m.current_reading), num(m.main_value), txt(m.unit),
      num(m.compare_value), num(m.diff), num(m.diff_pct),
    ]),
  ];
  appendAoaSheet(wb, aoa, "Compteurs", [
    { wch: 10 }, { wch: 22 }, { wch: 14 }, { wch: 14 }, { wch: 12 }, { wch: 12 },
    { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 8 },
  ]);
};

// Section: "Matrice pondérations" (PonderationMatrice) — 2D matrix.
// ponderationData = { columns:[{code,name}], rows:[{cost,designation,cle,values:[]}] }.
const buildPonderationSheet = (wb, data) => {
  if (!data) return;
  const columns = data.columns || [];
  const rows = data.rows || [];
  // Header row 1: fixed labels + each destination code. Header row 2: destination names.
  const headerCodes = styleRow(
    ["Nr de compte", "Désignation du compte", "Clé", ...columns.map((c) => txt(c.code))],
    HEADER_STYLE,
  );
  const headerNames = styleRow(
    ["", "", "", ...columns.map((c) => txt(c.name))],
    HEADER_STYLE,
  );
  const aoa = [
    ["Matrice pondérations"],
    [],
    headerCodes,
    headerNames,
    ...rows.map((r) => [
      txt(r.cost), txt(r.designation), txt(r.cle),
      ...(r.values || []).map((v) => num(v)),
    ]),
  ];
  const cols = [{ wch: 12 }, { wch: 26 }, { wch: 14 }, ...columns.map(() => ({ wch: 12 }))];
  appendAoaSheet(wb, aoa, "Matrice pondérations", cols);
};

// Section: "Données climatiques (ETC/DJF/NJ)" (EtcDjfNj). Merge the 3 arrays by
// start_date|end_date into one row per period; totals row at the end.
const buildEtcDjfNjSheet = (wb, data) => {
  if (!data) return;
  const etc = data.etc || [];
  const djf = data.djf || [];
  const nj = data.nj || [];
  const totals = data.totals || {};
  // Merge by the period key, mirroring EtcDjfNj.jsx mergeByPeriod.
  const byKey = new Map();
  const put = (arr, field) => {
    for (const r of arr) {
      const key = `${r.start_date}|${r.end_date}`;
      if (!byKey.has(key)) byKey.set(key, { start_date: r.start_date, end_date: r.end_date });
      byKey.get(key)[field] = r.value;
    }
  };
  put(etc, "etc"); put(djf, "djf"); put(nj, "nj");
  const merged = [...byKey.values()].sort(
    (a, b) => new Date(a.start_date) - new Date(b.start_date),
  );
  const aoa = [
    ["Données climatiques (ETC / DJF / NJ)"],
    [],
    styleRow(["Début", "Fin", "ETC", "DJF", "NJ"], HEADER_STYLE),
    ...merged.map((r) => [
      fmtDateCell(r.start_date), fmtDateCell(r.end_date), num(r.etc), num(r.djf), num(r.nj),
    ]),
    styleRow(["Total", "", num(totals.etc), num(totals.djf), num(totals.nj)], TOTAL_STYLE),
  ];
  appendAoaSheet(wb, aoa, "Données climatiques", [
    { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
  ]);
};

/**
 * Build and save the Répartition Results workbook from data already fetched into
 * the report preview. Pure function of its inputs (no network).
 *
 * @param {object}  p
 * @param {object}  p.modulesData           { modules, totals }        — "Modules"
 * @param {object}  p.surfacesData          { surfaces, subtotals, grand_total } — "Surfaces"
 * @param {object}  p.costSummaryData       { costs, totals }           — "Résumé des coûts"
 * @param {object}  p.costDetailsData       { details, per_invoice_data_available, totals } — "Coûts"
 * @param {object}  p.metersData            { meters }                  — "Compteurs"
 * @param {object}  p.ponderationData       { columns, rows }           — "Matrice pondérations"
 * @param {object}  p.etcDjfNjData          { etc, djf, nj, totals }    — "Données climatiques"
 * @param {object}  p.resultsPerModuleData  { modules, totals }
 * @param {object}  p.resultsPerObjectData  { objects, subtotals, grand_total }
 * @param {object}  p.resultsPerTenantData  { objects, subtotals, grand_total }
 * @param {object}  p.objectDetailData      { details: [...] }
 * @param {object}  p.periodYears           { current, prior } — dynamic year column labels
 * @param {string}  p.periodLabel           "DD.MM.YYYY - DD.MM.YYYY" fallback for detail Période
 * @param {boolean} p.acomptesEnabled       when false, omit Acomptes/Solde cols & rows
 * @param {string}  p.fileName              output filename (…\.xlsx)
 * @param {boolean} [p.save=true]           save to disk; when false, return the workbook
 * @returns {object|undefined} the workbook when save:false (for tests), else undefined
 */
export function exportRepartitionExcel({
  modulesData,
  surfacesData,
  costSummaryData,
  costDetailsData,
  metersData,
  ponderationData,
  etcDjfNjData,
  resultsPerModuleData,
  resultsPerObjectData,
  resultsPerTenantData,
  objectDetailData,
  // Phase 4: module détaillé + the two building-level gates. Defaults keep the
  // legacy workbook byte-identical — module off, objet on.
  moduleDetailData = null,
  tenantDetailData = null,
  moduleDetailEnabled = false,
  objectDetailEnabled = true,
  tenantDetailEnabled = false,
  periodYears,
  periodLabel,
  acomptesEnabled = true,
  fileName = "Repartition_Results.xlsx",
  save = true,
} = {}) {
  const wb = XLSX.utils.book_new();
  const usedNames = new Set();

  // Report data sections (mirror the on-screen page order), then the Results sheets.
  buildModulesSummarySheet(wb, modulesData, periodYears);
  buildSurfacesSheet(wb, surfacesData);
  buildCostSummarySheet(wb, costSummaryData, periodYears);
  buildCostDetailsSheet(wb, costDetailsData);
  buildMetersSheet(wb, metersData, periodYears);
  buildPonderationSheet(wb, ponderationData);
  buildEtcDjfNjSheet(wb, etcDjfNjData);

  // Sheet order mirrors the PDF section order exactly (Phase 4): each détaillé
  // follows its own summary, and a toggled-off détaillé contributes no sheets.
  buildModuleSheet(wb, resultsPerModuleData);
  usedNames.add("Module");

  if (moduleDetailEnabled) {
    buildObjectDetailSheets(wb, moduleDetailData, periodLabel, acomptesEnabled, usedNames, 'module');
  }

  buildObjectLikeSheet(wb, resultsPerObjectData, "Objet", "Résultats par objet", "module_code", acomptesEnabled);
  usedNames.add("Objet");

  if (objectDetailEnabled) {
    buildObjectDetailSheets(wb, objectDetailData, periodLabel, acomptesEnabled, usedNames, 'object');
  }

  buildObjectLikeSheet(wb, resultsPerTenantData, "Locataire", "Résultats par locataire", "tenant_name", acomptesEnabled);
  usedNames.add("Locataire");

  if (tenantDetailEnabled) {
    buildObjectDetailSheets(wb, tenantDetailData, periodLabel, acomptesEnabled, usedNames, 'tenant');
  }

  // Guard: a workbook with no sheets throws on write.
  if (!wb.SheetNames.length) {
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([["Aucune donnée"]]), "Résultats");
  }

  if (save) {
    XLSX.writeFile(wb, fileName);
    return undefined;
  }
  return wb;
}

// Build the export filename the same way the PDF export does, so both downloads
// share one naming convention (Repartition_<Building>_<years>.xlsx).
export function buildExcelFileName(buildingName, startDate, endDate) {
  const safeName = (buildingName || "report")
    .replace(/[\\/:*?"<>|.\s]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
  const years = fmtYearFile(startDate, endDate) || "report";
  return `Repartition_${safeName || "report"}_${years}.xlsx`;
}
